// ONBOARDING — batch 5's rebuild of Onboarding (UI rebuild; drawn when lib/ui-v2.ts switches batch 5 on).
//
// KEEP THE BEHAVIOUR, CHANGE THE LAYOUT. The reads are today's (screens/Onboarding.tsx): the leads, the potential leads
// and the clients, each read every minute with the same query keys; the search (a POST, 300 ms after the last key, two
// characters at least, never in the address); a queue read on past its first page (?queue= ?from=); the open record
// (?lead= ?tab= ?ask=). The rules its status lines keep are today's too: a count that could not be read is never a 0;
// every age is counted to the read; "Nobody needs you" only after a good fresh read; a failed read keeps what it had,
// said to be the earlier read.
//
// WHAT CHANGED (Astra, onboarding, under RULING-LIGHT): search first; the three views as tabs with their counts, Needs
// you by default, its queues as chips with theirs; ONE register — a person a row: the name and how to reach them, the
// HEADLINE (Buyer / Seller · amount coin · method — the operator, 27 Oct), what is missing, the stage with its age; under
// them the seven checklist sections as compact chips (the four older checks when the worker sends none), each line one
// tap away. The whole row opens the record (components/v2/onboarding/ClientRecord.tsx). Decisions stay in the Telegram
// ops group: this screen decides nothing.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { ChevronRight, Search, X } from 'lucide-react';
import type { BotRefusal, LeadChannel, LeadLabelKey, LeadQueue, OnboardingLeads } from '../../lib/api';
import { fmtAmount, fmtDate, fmtDay } from '../../lib/format';
import { offlineFailure } from '../../lib/read-state';
import { EmptyState, PageHead, Panel, SkeletonRows, StateLine, Tabs } from '../../components/v2/kit';
import type { StateTone } from '../../components/kit';
import {
  CardButton, ChatButton, Parts, READ_EVERY_MS, ago, failLine, failMore, failSaid, failWords, readOf, sentenceOf, targetOf,
  type Read,
} from '../../components/OnboardingParts';
import { cardClick } from '../Onboarding';
import {
  CHECKS, FOUND_WORD, explorerName, liveLine, networkWord, noWalletYet, onboardingApi, saleCoin, waitingWords,
  type Assessment, type Check, type Checklist, type ChecklistKey, type Claim, type ClientRow, type ClientsList, type ElsewherePerson, type Facts,
  type FoundBy, type LeadRow, type LeadsList, type ProspectRow, type ProspectsList, type ReadAs, type SearchResult, type WaitingOn, type WalletCheck,
  type RowWallet, type WalletLine, type WalletLive,
} from '../../lib/onboarding';
import { shortAddress } from '../../lib/custody-format';
import { ChecksLine, drawnOf, tradeParts, usable } from '../../components/v2/onboarding/Checklist';
import { Pill } from '../../components/Pill';
import { ClientRecord, type Fold, type RowSummary } from '../../components/v2/onboarding/ClientRecord';

/** The screen looks at its own clock this often, so a list that has stopped refreshing turns stale on screen. */
const TICK_MS = 30_000;

type View = 'needs' | 'potential' | 'clients';
type Chip = 'stuck' | 'waiting' | 'approved' | 'escalated';
type Count = keyof OnboardingLeads['counts'];
type SetParams = (set: Record<string, string | null>) => void;
type Tone = 'bad' | 'warn' | null;
type Strip = { text: string; tone: 'bad' | 'warn' };

/** The four queues in the worker's order: the address's word, the stage, the queue's name and short name, its count, and
 *  its own labels — whose tone (the worker's thresholds) colours the stage. */
const QUEUES: ReadonlyArray<{ queue: LeadQueue; chip: Chip; stage: string; name: string; short: string; items: string; count: Count; own: LeadLabelKey[] }> = [
  { queue: 'stuck_no_card', chip: 'stuck', stage: 'Stuck', name: 'Stuck — the card could not be sent', short: 'Stuck', items: 'stuck applications', count: 'stuck', own: ['stuck'] },
  { queue: 'awaiting_decision', chip: 'waiting', stage: 'Waiting', name: 'Waiting for a decision', short: 'Waiting', items: 'waiting cards', count: 'awaitingDecision', own: ['waiting'] },
  { queue: 'approved_not_contacted', chip: 'approved', stage: 'Approved · not contacted', name: 'Approved — not contacted', short: 'Not contacted', items: 'approved clients',
    count: 'approvedNotContacted', own: ['approved_not_contacted', 'contact_not_recorded'] },
  { queue: 'escalation_open', chip: 'escalated', stage: 'Escalated', name: 'Escalations open', short: 'Escalated', items: 'escalations', count: 'escalationsOpen', own: ['escalated'] },
];
type QueueInfo = (typeof QUEUES)[number];

const CHANNEL_WORD: Record<LeadChannel, string> = { business: 'Business', web: 'Web', 'bot-dm': 'Bot DM' };
/** The intake's questions as words (onboarding-agent src/onboarding/intake.ts): what a potential lead still has to answer. */
const MISSING_WORD: Record<string, string> = {
  side: 'buy or sell', asset: 'which crypto', size: 'size', frequency: 'how often', settlement_out: 'how they want to be paid',
  beneficiary: 'who receives the payment', full_name: 'full name', passport_country: 'nationality', jurisdiction: 'where they live',
  uae_resident: 'UAE residence', physically_in_uae: 'whether they are in the UAE', on_own_behalf: 'whether it is their own money',
  wallet_address: 'wallet', wallet_chain: 'wallet network', wallet_origin: 'where the wallet is from', source_of_funds: 'source of funds',
  purpose: 'purpose', referred_by: 'who referred them',
};
/** The bot's reasons for refusing someone, in words (onboarding-agent's codes). */
const REFUSAL_WORD: Record<string, string> = {
  below_minimum: 'below the minimum', residency: 'not a UAE resident in Dubai', tourist_buy: 'a tourist buying',
  tourist_not_tier1: 'a tourist outside the exceptions', unsupported_request: 'a request the desk does not take',
  sanctioned_passport: 'a sanctioned passport', third_party: "someone else's or a company's money", anonymous: 'refused verification',
  exceeds_first_trade_max: 'a first buy over the cap', previously_declined: 'declined before', other: 'other',
};
/** A code as words — own entries only: a code is the worker's or the customer's text. */
const own = (map: Record<string, string>, code: string, otherwise: (code: string) => string): string =>
  (Object.hasOwn(map, code) ? map[code] ?? otherwise(code) : otherwise(code));
const missingWord = (id: string) => own(MISSING_WORD, id, (c) => c);
const refusalWord = (code: string) => own(REFUSAL_WORD, code, () => 'other');

const count = (n: number) => fmtAmount(n, undefined, 0);
const plural = (n: number, one: string, many: string) => `${count(n)} ${n === 1 ? one : many}`;
const dayWords = (n: number) => (n === 0 ? 'under a day' : plural(n, 'day', 'days'));

/** How long a lead has sat in its queue, counted to the moment the list was READ (whole 24-hour days, the worker's rule). */
function shortAge(l: LeadRow, readMs: number): string {
  const since = l.since ? Date.parse(l.since) : NaN;
  if (!Number.isFinite(since) || !Number.isFinite(readMs)) return l.ageDays !== null ? `${l.ageDays}d` : 'age not known';
  const ms = Math.max(0, readMs - since);
  if (ms < 3_600_000) return '<1h';
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h`;
  return `${Math.floor(ms / 86_400_000)}d`;
}
/** How long before the read an instant was — counted to the BFF's clock, never the viewer's. */
function atRead(iso: string, readMs: number): string {
  const t = Date.parse(iso);
  return Number.isFinite(t) && Number.isFinite(readMs) ? ago(Math.max(0, readMs - t)) : fmtDate(iso);
}
/** Renders the screen again every so often: "Updated 2 min ago" keeps counting, and a stale list turns stale on screen. */
function useTick(everyMs: number): void {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), everyMs);
    return () => clearInterval(t);
  }, [everyMs]);
}

// ── What a row says ─────────────────────────────────────────────────────────────────────────────────────────────

/** The lead's stage and its age — "Waiting · 3d · wrote 2×" — coloured by the worker's own labels for these. */
function standing(l: LeadRow, readMs: number): { text: string; pill: string; wait: string; tone: Tone; why: string | null } {
  const q = QUEUES.find((x) => x.queue === l.queue)!;
  const notRecorded = l.queue === 'approved_not_contacted' && l.labels.some((x) => x.key === 'contact_not_recorded');
  const stage = notRecorded ? 'Approved · contact not recorded' : q.stage;
  const wrote = l.chasedTimes ? ` · wrote ${count(l.chasedTimes)}×` : '';
  const tones = l.labels.filter((x) => q.own.includes(x.key) || x.key === 'chased').map((x) => x.tone);
  // An approval nobody contacted is a problem whatever the worker's age threshold says: never plain.
  const tone: Tone = tones.includes('bad') ? 'bad' : tones.includes('warn') || l.queue === 'approved_not_contacted' ? 'warn' : null;
  const why = l.queue === 'escalation_open' && l.escalationReason ? l.escalationReason.replace(/_/g, ' ') : null;
  const pill = notRecorded ? 'Contact not recorded' : q.short;
  return { text: `${stage} · ${shortAge(l, readMs)}${wrote}`, pill, wait: `${shortAge(l, readMs)}${wrote}`, tone, why };
}

const checkSays = (c: Check | null | undefined, status: Check['status'], words: string | null): boolean =>
  !!c && c.status === status && (words === null || (c.reason ?? '').includes(words));

/** Which checklist section says a worker's label, when the checklist is there. A label is left off the row only when its
 *  section says it — look or failed; a section that reads "checked" against the label does not, and the label stays. */
const SECTION_OF: Partial<Record<LeadLabelKey, ChecklistKey>> = {
  wallet_severe: 'wallet', wallet_high: 'wallet', not_scored: 'wallet', sanctions: 'wallet', changed_after_card: 'mismatch',
  not_resident: 'presence', not_in_uae: 'presence', not_own_behalf: 'funds', over_cap: 'requirement', wallet_reused: 'history',
  one_id_image: 'id',
};
const sectionSays = (c: Checklist, key: ChecklistKey): boolean => {
  const s = c.sections.find((x) => x.key === key);
  return !!s && (s.status === 'look' || s.status === 'failed' || (s.status === 'pending' && key === 'wallet'));
};

/** Whether the row already says what the worker's label says — its stage, the chat button, a check or a section. Only
 *  then is the label left off the row's warning line: each fact once, and a warning nothing else says always stays. */
function saidOnRow(l: LeadRow, key: LeadLabelKey): boolean {
  const q = QUEUES.find((x) => x.queue === l.queue);
  if (q?.own.includes(key)) return true;
  if (key === 'chased') return !!l.chasedTimes;
  if (key === 'returning') return true;
  // Said on the Missing line, as a quiet word (leadPerson's noPath).
  if (key === 'no_contact_path') return true;
  if (usable(l.checklist)) {
    const s = SECTION_OF[key];
    return s ? sectionSays(l.checklist, s) : false;
  }
  const a = l.assessment;
  switch (key) {
    case 'not_resident':    return checkSays(a?.residency, 'flagged', 'Not a UAE resident');
    case 'not_in_uae':      return checkSays(a?.residency, 'flagged', 'Not in the UAE now');
    case 'not_own_behalf':  return checkSays(a?.funds, 'flagged', 'Not their own money');
    case 'one_id_image':    return checkSays(a?.kyc, 'pending', '1 of 2 ID images');
    case 'sanctions':       return checkSays(a?.wallet, 'flagged', 'On a sanctions list');
    case 'wallet_severe':   return checkSays(a?.wallet, 'flagged', 'Risk severe');
    case 'wallet_high':     return checkSays(a?.wallet, 'flagged', 'Risk high');
    case 'not_scored':      return checkSays(a?.wallet, 'pending', null);
    default:                return false;
  }
}
/** The worker's own warnings the row does not say, the worst first — one line, red when one is bad. */
function flagsOf(l: LeadRow): Strip | null {
  const flags = l.labels.filter((x) => !saidOnRow(l, x.key) && (x.tone === 'bad' || x.tone === 'warn'));
  if (flags.length === 0) return null;
  const bad = flags.filter((x) => x.tone === 'bad');
  return { text: [...bad, ...flags.filter((x) => x.tone === 'warn')].map((x) => x.text).join(' · '), tone: bad.length ? 'bad' : 'warn' };
}
/** The bot's refusal of an approved client since the approval — shown, never acted on ("Operator word is final"). */
function refusalStrip(r: BotRefusal | null, readMs: number): Strip | null {
  if (!r) return null;
  const what = r.compliance === true ? 'Refused by the bot' : r.compliance === false ? 'Declined by the bot' : 'Refused or declined by the bot';
  const when = r.at ? atRead(r.at, readMs) : 'when not known';
  const why = r.reasons === null ? 'reasons not known' : r.reasons.map(refusalWord).join(', ');
  return { text: `${what} ${when} — ${why}`, tone: r.compliance === false ? 'warn' : 'bad' };
}

/** What a row reads. */
interface Person {
  key: string;
  name: string;
  /** "Business · Telegram": the chat it came through and the app that reaches them. */
  source: string;
  /** The stage as a pill, and the wait beside it. */
  stage: { pill: string; wait: string | null; tone: Tone; title?: string | null } | null;
  claim: Claim | null;
  facts: Facts | null;
  wallet: WalletLine;
  /** The wallet's network and its explorer page, on the row (1 Nov 2026); null: no wallet given, or a worker older than it. */
  walletAt: RowWallet | null;
  /** What is missing — what the chat waits on them for, what they have still to answer — or what the row waits on. */
  missing: { text: string; tone: 'plain' | 'warn'; title?: string } | null;
  checklist: Checklist | null | undefined;
  assessment: Assessment | null;
  strip: Strip | null;
  /** No way to reach them outside the bot — said as a quiet word on the Missing line. */
  noPath: boolean;
  /** Their chat button and links — drawn on the row only when it opens no record (the only door to them); otherwise
   *  they are at the record's top. */
  chat: ReactNode;
  links: ReactNode;
  found?: FoundBy[];
  open?: () => void;
  isOpen?: boolean;
}

/** The name first: the one they gave, else their @username, else where they came from. */
function naming(name: string | null, who: string | null, channel: LeadChannel | null): { name: string; handle: string | null } {
  const handle = who?.startsWith('@') ? who : null;
  const first = name ?? handle ?? who ?? 'Not named';
  return { name: first, handle: name ? handle ?? (channel === 'web' ? 'Website' : channel ? 'Telegram' : null) : null };
}
const cardWallet = (live: WalletLive | null | undefined, check: WalletCheck | null, claim: Claim | null, noWallet: boolean, readMs: number): WalletLine =>
  liveLine(live, check, saleCoin(claim), noWallet, readMs);
/** The chat it came through and the app that reaches them: "Business · Telegram", "Web · WhatsApp". */
function sourceOf(p: { channel: LeadChannel | null; contactUrl: string | null }): string {
  const app = p.contactUrl?.startsWith('https://wa.me/') ? 'WhatsApp' : p.channel === 'web' && !p.contactUrl ? 'Website' : 'Telegram';
  return [p.channel ? CHANNEL_WORD[p.channel] : null, app].filter(Boolean).join(' · ');
}
const noPathOf = (p: { contactUrl: string | null; chatViaBot: boolean }) => !p.contactUrl && !p.chatViaBot;
const waitingMissing = (w: WaitingOn | null, channel: LeadChannel | null, readMs: number) => {
  if (!w) return null;
  const s = waitingWords(w, channel, readMs);
  return { text: s.text, tone: s.tone, title: s.text };
};

const STAGE_INK = { bad: 'text-neg', warn: 'text-warn' } as const;
const PILL = { bad: 'bad', warn: 'warn' } as const;
/** Astra's register: person · trade · missing · stage · chevron (a card on the phone). */
// ONE FACT PER COLUMN (his "one fact per place", 1 Nov 2026: the Trade column held side, amount, method, the wallet's coins,
// their read age, the network, the address and its explorer): Trade · Wallet · Missing each their own seat.
const GRID = 'grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_1rem] xl:grid-cols-[minmax(0,13rem)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,9rem)_1rem]';

/** The trade: who they are on it (Buyer / Seller), the size and coin as the figure, how it is paid as a quiet line. */
function TradeCell({ claim, size }: { claim: Person['claim']; size?: string | null }) {
  const t = tradeParts(claim, size);
  if (!t.side && !t.amount && !t.asset && !t.method) return <p className="text-body text-tg-hint">No trade on record</p>;
  return (
    // Who they are on it and the coin ("Seller · USDC"), then its size — the figure, its AED unit muted after it — then how
    // it is paid: three lines, one fact each.
    <div className="min-w-0" data-headline="">
      {(t.side || t.asset) && <p className="text-body"><span className="font-medium">{t.side ?? 'Trade'}</span>{t.asset && <span className="text-tg-hint"> · {t.asset}</span>}</p>}
      {t.amount && <p className="text-body"><SizeWords text={t.amount} /></p>}
      {t.method && <p className="text-meta text-tg-hint">{t.method}</p>}
    </div>
  );
}

/** The first thing in the way: the words up to the first " · " — the reminders, the bot's reach and the rest are the record's
 *  (Astra, 1 Nov 2026). The whole line stays the row's title. */
function blocker(text: string): string {
  return text.split(' · ')[0] ?? text;
}

/** A size in the claim's words: the figures in mono, the trailing AED unit muted ("up to 300,000 AED", or their own words). */
function SizeWords({ text }: { text: string }) {
  const m = /^(.*?)(\u00a0AED)$/.exec(text);
  if (!m) return <span>{text}</span>;
  return <><span className="font-mono tabular">{m[1]}</span><span className="text-meta text-tg-hint">{m[2]}</span></>;
}

/** The wallet: each coin it holds on its own line (figure, unit after), when it was read, then where it is — network,
 *  address, explorer — on one quiet line. */
function WalletCell({ line, at }: { line: Person['wallet']; at: Person['walletAt'] }) {
  const tone = (t: 'pos' | 'neg' | 'muted' | null) => (t === 'pos' ? 'text-pos' : t === 'neg' ? 'text-neg' : t === 'muted' ? 'text-tg-hint' : '');
  return (
    <div className="min-w-0" data-wallet="">
      {line && (line.kind === 'held'
        ? line.parts.map((p) => (
          <p key={p.asset} className={`whitespace-nowrap text-body ${tone(p.tone)}`}>
            <span className="font-mono tabular">{fmtAmount(p.amount, p.asset)}</span> <span className="text-meta">{p.asset}</span>
          </p>
        ))
        : <p className={`text-body ${tone(line.tone)}`}>{line.text}</p>)}
      {line?.when && <p className={`text-meta ${line.when.tone === 'warn' ? 'text-warn' : 'text-tg-hint'}`} data-live={line.when.tone === 'warn' ? 'warn' : 'muted'}>{line.when.text}</p>}
      {at && (
        <p className="text-meta text-tg-hint" data-wallet-at="">
          {networkWord(at.network)} · <span className="font-mono">{shortAddress(at.address, 6, 4)}</span>
          {at.explorer && <> · <a href={at.explorer} target="_blank" rel="noreferrer" className="text-tg-link hover:underline">{explorerName(at.explorer)} ↗</a></>}
        </p>
      )}
    </div>
  );
}

/** One person a row (Astra, onboarding): who, the trade, the wallet, what is missing with the checks in
 *  one line, the stage. The whole row opens the record, where the seven, the chat and the card are. */
function PersonRow({ p }: { p: Person }) {
  const drawn = drawnOf(p.checklist, p.assessment);
  // No way to reach them comes first: a cut line must not hide it.
  // "Ready for your decision" is the group's own heading: the row does not say it again (Astra, 1 Nov 2026).
  const missingText = p.missing?.text && !/^Ready for your decision$/i.test(p.missing.text.trim()) ? p.missing.text : null;
  const missing = [p.noPath ? 'No way to reach them outside the bot' : null, missingText].filter(Boolean).join(' · ');
  const warn = p.missing?.tone === 'warn' || p.noPath;
  return (
    <li aria-label={p.name} className="border-b border-stroke-soft last:border-b-0">
      <div className={`${GRID} px-4 py-4 xl:px-5 ${p.open ? 'cursor-pointer hover:bg-panel-2' : ''} ${p.isOpen ? 'bg-panel-2' : ''}`}
        onClick={p.open ? cardClick(p.open) : undefined} data-opens={p.open ? '' : undefined}>
        <div className="col-start-1 row-start-1 min-w-0">
          {p.open
            ? <button type="button" className="tap-44 block max-w-full truncate text-left text-body font-medium hover:underline" title={p.name} aria-label={`Open the record of ${p.name}`} onClick={p.open}>{p.name}</button>
            : <span className="block truncate text-body font-medium" title={p.name}>{p.name}</span>}
          {p.source && <p className="truncate text-meta text-tg-hint">{p.source}</p>}
          {p.found && p.found.length > 0 && <p className="text-meta text-tg-hint" data-found="">Found by {p.found.map((f) => FOUND_WORD[f]).join(' · ')}</p>}
          {/* A row that opens no record keeps its only ways to them. */}
          {!p.open && (p.chat || p.links) && <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2" data-ways="">{p.chat}{p.links}</div>}
        </div>
        <div className="col-start-2 row-start-1 self-start justify-self-end lg:col-start-3 xl:col-start-6">
          {p.open && <ChevronRight className="mt-0.5 h-4 w-4 text-tg-hint" aria-hidden />}
        </div>
        <div className="col-span-2 min-w-0 lg:col-span-1 lg:col-start-1 lg:row-start-2 xl:col-start-2 xl:row-start-1">
          <TradeCell claim={p.claim} size={p.facts?.size} />
        </div>
        <div className="col-span-2 min-w-0 lg:col-span-1 lg:col-start-2 lg:row-start-2 xl:col-start-3 xl:row-start-1">
          <WalletCell line={p.wallet} at={p.walletAt} />
        </div>
        <div className="col-start-1 min-w-0 lg:col-span-2 lg:row-start-3 xl:col-span-1 xl:col-start-4 xl:row-start-1">
          {/* WHAT IS MISSING IS NEVER CUT (the audit, 1 Nov 2026: "Funds failed · R…" — the reasons he decides on). */}
          {/* THE BLOCKER, NOT ITS HISTORY (Astra, 1 Nov 2026): the first thing in the way, whole; reminders and what the bot can do
              are the record's. The checks: every one that failed or needs a look, by name — what he decides on — and the rest
              counted. */}
          {missing && <p className={`break-words text-meta xl:text-body ${warn ? 'text-warn' : 'text-tg-hint'}`} title={p.missing?.title ?? missing} data-missing={warn ? 'warn' : 'plain'}>{blocker(missing)}</p>}
          {drawn && <ChecksLine drawn={drawn.rows} compact />}
          {p.strip && <p className={`break-words text-meta ${STAGE_INK[p.strip.tone]}`} title={p.strip.text} data-strip={p.strip.tone}>{p.strip.text}</p>}
        </div>
        <div className="col-start-2 min-w-0 self-start text-right lg:col-start-2 lg:row-start-1 xl:col-start-5 xl:row-start-1">
          {p.stage && (
            <div title={p.stage.title ?? undefined} data-standing={p.stage.tone ?? 'plain'}>
              <Pill tone={p.stage.tone ? PILL[p.stage.tone] : 'neutral'}>{p.stage.pill}</Pill>
              {p.stage.wait && <p className={`mt-1 text-meta ${p.stage.tone ? STAGE_INK[p.stage.tone] : 'text-tg-hint'}`}>{p.stage.wait}</p>}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

/** The register's column heads, from lg. */
function Heads() {
  return (
    <div className={`${GRID} hidden border-b border-stroke bg-panel-2 px-5 py-3 text-label font-medium uppercase tracking-wider text-tg-hint xl:grid`} aria-hidden>
      <span>Person</span><span>Trade</span><span>Wallet</span><span>Missing</span><span className="text-right">Stage</span><span />
    </div>
  );
}

/** A list's rows — and, above them, when no row carries a checklist or the four checks, why: a worker older than them. */
function PersonList({ label, people }: { label: string; people: Person[] }) {
  const none = people.length > 0 && people.every((p) => !drawnOf(p.checklist, p.assessment));
  return (
    <>
      {none && <div className="border-b border-stroke-soft px-4 py-3 lg:px-5" data-no-checks=""><StateLine tone="neutral">Checks come with the bot&apos;s next update.</StateLine></div>}
      <ol aria-label={label}>{people.map((p) => <PersonRow key={p.key} p={p} />)}</ol>
    </>
  );
}

/** A row's links: the card in the ops group, and — a client — their party in the Books. */
const cardLink = (url: string | null) => (url ? <CardButton url={url} /> : null);

// ── The screen ───────────────────────────────────────────────────────────────────────────────────────────────────

const FOLDS: readonly Fold[] = ['evidence', 'chat', 'history'];

export function OnboardingV2({ initialQuery = '' }: { initialQuery?: string } = {}) {
  useTick(TICK_MS);
  const qc = useQueryClient();
  const [search, setSearch] = useSearchParams();
  const asked = search.get('view');
  // ?view=approved stays the clients' key: links already made to it still open them.
  const view: View = asked === 'approved' ? 'clients' : asked === 'potential' ? 'potential' : 'needs';
  const setParams: SetParams = (set) => setSearch((prev) => {
    const next = new URLSearchParams(prev);
    for (const [k, v] of Object.entries(set)) if (v === null) next.delete(k); else next.set(k, v);
    return next;
  }, { replace: true });
  const openLead = search.get('lead');
  // ?tab= keeps today's words: details (now Evidence), chat, history.
  const tabWord = search.get('tab');
  const fold: Fold | null = tabWord === 'details' ? 'evidence' : FOLDS.find((f) => f === tabWord) ?? null;
  const asking = CHECKS.find((k) => k.key === search.get('ask'))?.key ?? null;
  const opening: Opening = {
    openLead,
    toggleLead: (id) => setParams({ lead: openLead === id ? null : id, tab: null, ask: null }),
  };
  const [typed, setTyped] = useState(initialQuery);
  const searching = typed.trim() !== '';
  const sought = useDebounced(typed.trim(), SEARCH_DEBOUNCE_MS);
  const fromWord = search.get('from') ?? '';
  const from = /^\d{1,6}$/.test(fromWord) ? Number(fromWord) : 0;

  // All three are read whatever the tab, so each tab's count stands on it.
  const q = useQuery({ queryKey: ['onboarding', 'leads'], queryFn: () => onboardingApi.leads(), refetchInterval: READ_EVERY_MS });
  const clientsFrom = view === 'clients' ? from : 0;
  const cq = useQuery({ queryKey: ['onboarding', 'clients', clientsFrom], queryFn: () => onboardingApi.clients(clientsFrom), refetchInterval: READ_EVERY_MS });
  const potentialFrom = view === 'potential' ? from : 0;
  const pq = useQuery({ queryKey: ['onboarding', 'prospects', potentialFrom], queryFn: () => onboardingApi.prospects(potentialFrom), refetchInterval: READ_EVERY_MS });
  const chosen = view === 'needs' ? QUEUES.find((s) => s.chip === search.get('queue')) ?? null : null;
  const pageFrom = chosen ? from : 0;
  const later = chosen !== null && pageFrom > 0;
  const paged = useQuery({
    queryKey: ['onboarding', 'leads', chosen?.queue ?? null, pageFrom],
    queryFn: () => onboardingApi.leads({ queue: chosen!.queue, offset: pageFrom }),
    enabled: later && !searching,
    refetchInterval: READ_EVERY_MS,
  });
  const pagedRead = readOf(paged, (p) => p.leads.length > 0);
  const sq = useQuery({
    queryKey: ['onboarding', 'search', sought],
    queryFn: () => onboardingApi.search(sought),
    enabled: searching && sought.length >= MIN_QUERY,
    refetchInterval: READ_EVERY_MS,
  });
  const leadsRead = readOf(q, (d) => d.leads.length > 0);
  const clientsRead = readOf(cq, (d) => d.clients.length > 0);
  const prospectsRead = readOf(pq, (d) => d.prospects.length > 0);
  const searchRead = readOf(sq, (d) => d.needsYou.length + d.potential.length + d.clients.length + d.elsewhere.length > 0);
  const tracking = (clientsRead.d ? clientsRead.d.contactTrackingSince : null) ?? leadsRead.d?.contactTrackingSince ?? null;
  // The open record, with what its row knows, from every list the screen holds — and, when none lists the person any more,
  // the summary last seen for them: a warning never drops off an open record because of what is typed above it.
  const found = openLead ? rowSummaryOf(openLead, {
    search: searching ? searchRead : null, view, leads: leadsRead, paged: later ? pagedRead : null, prospects: prospectsRead, clients: clientsRead, tracking,
  }) : null;
  const [lastRow, setLastRow] = useState<{ id: string; row: RowSummary } | null>(null);
  const foundKey = found ? JSON.stringify(found) : '';
  useEffect(() => {
    if (openLead && found) setLastRow((k) => (k && k.id === openLead && JSON.stringify(k.row) === foundKey ? k : { id: openLead, row: found }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openLead, foundKey]);
  const openRow: RowSummary | null = found ?? (openLead && lastRow?.id === openLead ? lastRow.row : null);
  const active = searching ? searchRead : view === 'clients' ? clientsRead : view === 'potential' ? prospectsRead : leadsRead;
  const activeQ: UseQueryResult<unknown> = searching ? sq : view === 'clients' ? cq : view === 'potential' ? pq : q;
  const needs = needsTotal(leadsRead.d);
  const clients = clientsRead.d && clientsRead.d.counts.approvedHere !== null && clientsRead.d.counts.imported !== null
    ? clientsRead.d.counts.approvedHere + clientsRead.d.counts.imported : null;
  // A count that could not be read is not drawn — never a 0.
  const tabs = [
    { id: 'needs', label: 'Needs you', to: '/onboarding', count: leadsRead.d ? needs : null },
    { id: 'potential', label: 'Potential', to: '/onboarding?view=potential', count: prospectsRead.d ? prospectsRead.d.total : null },
    { id: 'clients', label: 'Clients', to: '/onboarding?view=approved', count: clientsRead.d ? clients : null },
  ];

  return (
    <>
      <PageHead
        title="Onboarding"
        sub={
          <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <NeedsSentence q={q} read={leadsRead} />
            {/* When the list is stale its notice says when it was read — once (Astra, 1 Nov 2026). */}
            {active.d && active.fresh && (
              <span>
                Updated {Number.isFinite(active.readMs) ? ago(Math.max(0, Date.now() - active.readMs)) : 'at a time not known'}
              </span>
            )}
          </span>
        }
        // Refresh as every screen has it: a button in the head, top right (the audit, 1 Nov 2026: a link inside the subtitle).
        actions={(
          <button type="button" className="btn-secondary px-3 text-body disabled:opacity-50" disabled={activeQ.isFetching}
            onClick={() => qc.invalidateQueries({ queryKey: ['onboarding'] })}>
            {activeQ.isFetching ? 'Reading…' : 'Refresh'}
          </button>
        )}
      />

      <SearchBox typed={typed} setTyped={setTyped} />

      {searching ? (
        <SearchView typed={typed.trim()} sought={sought} q={sq} read={searchRead} tracking={tracking} {...opening} />
      ) : (
        <>
          <Tabs items={tabs} current={view} label="Onboarding views" wrap />
          {view === 'needs'
            ? <NeedsView q={q} read={leadsRead} paged={paged} pagedRead={pagedRead} chosen={chosen} from={pageFrom} later={later} setParams={setParams} {...opening} />
            : view === 'potential'
              ? <PotentialView q={pq} read={prospectsRead} from={potentialFrom} setParams={setParams} {...opening} />
              : <ClientsView q={cq} read={clientsRead} from={clientsFrom} setParams={setParams} trackingFallback={leadsRead.d?.contactTrackingSince ?? null} {...opening} />}
        </>
      )}

      {openLead && (
        <ClientRecord id={openLead} row={openRow} fold={fold} setFold={(f) => setParams({ tab: f })}
          asking={asking} setAsking={(k) => setParams({ ask: k })}
          close={() => setParams({ lead: null, tab: null, ask: null })} />
      )}
    </>
  );
}

// ── The search ───────────────────────────────────────────────────────────────────────────────────────────────────

const MIN_QUERY = 2;
const SEARCH_DEBOUNCE_MS = 300;
const MAX_QUERY = 100;

function useDebounced<T>(value: T, ms: number): T {
  const [held, setHeld] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setHeld(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return held;
}

/** The search box: one line, its own clear (×), and Esc to clear — which brings the tabs back; an open record stays. */
function SearchBox({ typed, setTyped }: { typed: string; setTyped: (s: string) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div className="relative mb-4" role="search">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tg-hint" aria-hidden />
      <input ref={ref} type="text" inputMode="search" enterKeyHint="search" autoComplete="off" autoCorrect="off" spellCheck={false}
        maxLength={MAX_QUERY} value={typed} onChange={(e) => setTyped(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape' && typed) { e.preventDefault(); e.stopPropagation(); setTyped(''); } }}
        placeholder="Search name, phone or ref" aria-label="Search the onboarding lists"
        className="input w-full text-ellipsis !pl-9 !pr-12" data-search="" />
      {typed && (
        <button type="button" aria-label="Clear the search" title="Clear (Esc)" data-clear=""
          className="absolute right-0.5 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-md text-tg-hint hover:text-tg-text lg:h-10 lg:w-10"
          onClick={() => { setTyped(''); ref.current?.focus(); }}>
          <X className="h-4 w-4" aria-hidden />
        </button>
      )}
    </div>
  );
}

const READ_AS_WORD: Record<ReadAs, string> = {
  name: 'a name or @username', telegram_id: 'a Telegram id', ref: 'a card ref', phone: 'a phone number', email: 'an email',
};
const STANDING_WORD: Record<NonNullable<ElsewherePerson['standing']>, { text: string; tone: Tone }> = {
  rejected: { text: 'Rejected', tone: 'bad' }, refused: { text: 'Refused by the bot', tone: 'bad' }, blocked: { text: 'Blocked', tone: 'bad' },
  chatting: { text: 'Chatting — no trade named yet', tone: null }, other: { text: 'Not on any list', tone: null },
};

/** The results, where the tabs were: how many and how the query was read, then Needs you · Potential · Clients · Not on a
 *  list, each with its count. Never "nobody found" for a search that has not run, or for matches it could not show. */
function SearchView({ typed, sought, q, read, tracking, ...o }: {
  typed: string; sought: string; q: UseQueryResult<SearchResult>; read: Read<SearchResult>; tracking: string | null;
} & Opening) {
  const { d, readMs } = read;
  const pending = typed !== sought;
  const head = (text: ReactNode, tone: StateTone = 'neutral', more?: ReactNode) => (
    <div role="status" className="mb-4" data-search-note=""><StateLine tone={tone} more={more}>{text}</StateLine></div>
  );
  if (typed.length < MIN_QUERY) return head('Type at least 2 characters.');
  if (!d || pending) {
    if (!pending && q.isError) { const f = failSaid("Couldn't search", q.error); return head(f.line, 'warn', f.more); }
    if (!pending && q.fetchStatus === 'paused') return head('No connection — searching when back.', 'warn');
    return head('Searching…');
  }
  const sections = [
    { key: 'needs', title: 'Needs you', people: d.needsYou.map((r) => ({ ...leadPerson(r.lead, readMs, o), found: r.found, key: `n:${r.lead.id ?? r.lead.ref}` })) },
    { key: 'potential', title: 'Potential', people: d.potential.map((r) => ({ ...prospectPerson(r.prospect, readMs, o), found: r.found, key: `p:${r.prospect.id ?? r.prospect.ref}` })) },
    { key: 'clients', title: 'Clients', people: d.clients.map((r, i) => ({ ...clientPerson(r.client, i, tracking, readMs, o), found: r.found, key: `c:${r.client.id ?? r.client.customerId ?? i}` })) },
  ];
  const nobody = d.needsYou.length + d.potential.length + d.clients.length + d.elsewhere.length === 0;
  const lost = d.unreadable > 0 ? d.unreadable : 0;
  const readAs = d.readAs.length ? ` — read as ${d.readAs.map((x) => READ_AS_WORD[x]).join(', ')}` : '';
  const total = d.total === null ? 'People found' : d.more ? `The first ${count(d.total)} found` : d.total === 1 ? '1 person found' : `${count(d.total)} people found`;
  return (
    <section className="space-y-4" aria-label="Search results">
      {q.isError && head(<>Search refresh failed — read {read.readWords}.</>, 'warn', failMore(q.error))}
      {nobody && !lost
        ? <p className="text-body" role="status" data-search-summary="">Nobody found{readAs}.</p>
        : !nobody && <p className="text-body" role="status" data-search-summary="">{total}{readAs}.</p>}
      {lost > 0 && head(nobody
        ? <>{lost === 1 ? '1 match' : `${count(lost)} matches`} found, but not shown.</>
        : <>{lost === 1 ? '1 more match' : `${count(lost)} more matches`} could not be shown.</>, 'warn',
        `The worker sent ${lost === 1 ? 'it' : 'them'} in a shape this screen cannot read${nobody ? readAs : ''}.`)}
      {d.more && <p className="text-body text-warn" data-search-more="">More than 50 found — type more</p>}
      {!nobody && (
        <>
          {sections.filter((s) => s.people.length > 0).map((s) => (
            <Panel key={s.key} flush title={s.title} aside={count(s.people.length)}>
              <Heads />
              <PersonList label={`Found on ${s.title}`} people={s.people} />
            </Panel>
          ))}
          {d.elsewhere.length > 0 && (
            <Panel flush title="Not on a list" aside={count(d.elsewhere.length)}>
              <ul aria-label="Found on no list">
                {d.elsewhere.map((r, i) => <ElsewhereRow key={r.person.id ?? r.person.customerId ?? `e:${i}`} p={r.person} found={r.found} readMs={readMs} o={o} />)}
              </ul>
            </Panel>
          )}
        </>
      )}
    </section>
  );
}

/** What a person's row says to their record — from the lists the screen holds, the first that lists them. PURE. */
function rowSummaryOf(id: string, src: {
  search: Read<SearchResult> | null; view: View; leads: Read<LeadsList>; paged: Read<LeadsList> | null;
  prospects: Read<ProspectsList>; clients: Read<ClientsList>; tracking: string | null;
}): RowSummary | null {
  const fromSearch = () => (src.search?.d ? searchRow(src.search.d, id, src.search.readMs, src.tracking) : null);
  const fromLeads = () => {
    for (const r of [src.paged, src.leads]) {
      const l = r?.d?.leads.find((x) => x.id === id);
      if (l && r) return leadSummary(l, r.readMs);
    }
    return null;
  };
  const fromProspects = () => {
    const p = src.prospects.d?.prospects.find((x) => x.id === id);
    return p ? prospectSummary(p, src.prospects.readMs) : null;
  };
  const fromClients = () => {
    const c = src.clients.d?.clients.find((x) => x.id === id);
    return c ? clientSummary(c, src.tracking, src.clients.readMs) : null;
  };
  const tabs = src.view === 'potential' ? [fromProspects, fromLeads, fromClients]
    : src.view === 'clients' ? [fromClients, fromLeads, fromProspects] : [fromLeads, fromProspects, fromClients];
  for (const f of [fromSearch, ...tabs]) {
    const row = f();
    if (row) return row;
  }
  return null;
}
const leadSummary = (l: LeadRow, readMs: number): RowSummary => {
  const s = standing(l, readMs);
  return { standing: { text: s.text, tone: s.tone }, strip: flagsOf(l), claim: l.claim };
};
const prospectSummary = (p: ProspectRow, readMs: number): RowSummary => ({ standing: { text: prospectStanding(p, readMs).text, tone: null }, strip: null, claim: p.claim });
const clientSummary = (c: ClientRow, tracking: string | null, readMs: number): RowSummary =>
  ({ standing: clientStanding(c, tracking), strip: refusalStrip(c.refusedByBot, readMs), claim: c.claim });

function searchRow(d: SearchResult, id: string, readMs: number, tracking: string | null): RowSummary | null {
  const l = d.needsYou.find((r) => r.lead.id === id)?.lead;
  if (l) return leadSummary(l, readMs);
  const p = d.potential.find((r) => r.prospect.id === id)?.prospect;
  if (p) return prospectSummary(p, readMs);
  const c = d.clients.find((r) => r.client.id === id)?.client;
  if (c) return clientSummary(c, tracking, readMs);
  const e = d.elsewhere.find((r) => r.person.id === id)?.person;
  return e?.standing ? { standing: STANDING_WORD[e.standing], strip: null, claim: null } : null;
}

/** A person found on no list, on one line: who, where they stand, last active (to the read), what they were found by. */
function ElsewhereRow({ p, found, readMs, o }: { p: ElsewherePerson; found: FoundBy[]; readMs: number; o: Opening }) {
  const n = naming(p.name, p.who, p.channel);
  const st = p.standing ? STANDING_WORD[p.standing] : null;
  const open = p.id ? () => { if (o.openLead !== p.id) o.toggleLead(p.id!); } : undefined;
  return (
    <li aria-label={n.name} data-elsewhere={p.standing ?? 'unknown'} className="border-b border-stroke-soft last:border-b-0">
      <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 lg:px-5 ${open ? 'cursor-pointer hover:bg-panel-2' : ''}`}
        onClick={open ? cardClick(open) : undefined} data-opens={open ? '' : undefined}>
        {open
          ? <button type="button" className="tap-44 min-w-0 break-words text-left text-body font-medium hover:underline" aria-label={`Open the record of ${n.name}`} onClick={open}>{n.name}</button>
          : <span className="min-w-0 break-words text-body font-medium">{n.name}</span>}
        {n.handle && <span className="min-w-0 truncate text-meta text-tg-hint">{n.handle}</span>}
        <span className={`text-meta ${st?.tone === 'bad' ? 'text-neg' : 'text-tg-text'}`} data-standing="">{st ? st.text : 'Standing not known'}</span>
        {p.lastActiveAt && <span className="text-meta text-tg-hint">last active {atRead(p.lastActiveAt, readMs)}</span>}
        {found.length > 0 && <span className="text-meta text-tg-hint" data-found="">Found by {found.map((f) => FOUND_WORD[f]).join(' · ')}</span>}
      </div>
    </li>
  );
}

function needsTotal(d: OnboardingLeads | LeadsList | undefined): number | null {
  if (!d) return null;
  let n = 0;
  for (const s of QUEUES) {
    const c = d.counts[s.count];
    if (c === null) return null;
    n += c;
  }
  return n;
}

/** How many people need you and how long the oldest has waited (to the read). Green only when earned. */
function NeedsSentence({ q, read }: { q: UseQueryResult<LeadsList>; read: Read<LeadsList> }) {
  const { d, readMs, fresh, readWords } = read;
  const line = (text: string, tone: 'good' | 'warn' | 'plain') => (
    <span className={tone === 'good' ? 'text-pos' : tone === 'warn' ? 'text-warn' : 'text-tg-text'} data-needs={tone}>{text}</span>
  );
  if (!d) {
    // Offline, the failed first read is the paused one (lib/read-state.ts offlineFailure).
    if (q.fetchStatus === 'paused' || (q.isError && offlineFailure(q.error))) return line('No connection — reading when back.', 'warn');
    if (q.isError) return line('Who needs you could not be read.', 'warn');
    return line('Reading who needs you…', 'plain');
  }
  const total = needsTotal(d);
  // When it was read stands beside it ("Updated …", amber once stale): the sentence never passes for now.
  if (total === null) return line('Some counts could not be read.', 'warn');
  if (total === 0) {
    if (d.unreadable !== 0) return line('Nobody listed — some records unread.', 'warn');
    return fresh && !q.isError && q.fetchStatus !== 'paused'
      ? line('Nobody needs you right now.', 'good')
      : line(`Nobody needed you, read ${readWords}.`, 'plain');
  }
  let oldest = d.oldestWaitingDays ?? -1;
  for (const l of d.leads) {
    const since = l.since ? Date.parse(l.since) : NaN;
    const days = Number.isFinite(since) && Number.isFinite(readMs) ? Math.floor(Math.max(0, readMs - since) / 86_400_000) : l.ageDays;
    if (days !== null && days > oldest) oldest = days;
  }
  const who = total === 1 ? '1 person needs you' : `${count(total)} people need you`;
  return line(`${who}${oldest >= 0 ? ` · oldest ${dayWords(oldest)}` : ''}`, 'warn');
}

/** What is wrong with a list's read, above it — ONE line: no connection, a failed read (keeping what it had, said to
 *  be the earlier read), a read more than ten minutes old; a first read on its way is the skeleton. */
function ReadLines({ q, read, noun }: { q: UseQueryResult<unknown>; read: Read<unknown>; noun: string }) {
  const { d, fresh, readWords } = read;
  const box = (line: ReactNode, more?: ReactNode, action?: ReactNode) => (
    <div className="mb-4 rounded-lg border border-warn/25 bg-warn-bg px-4 py-3" data-box=""><StateLine tone="warn" more={more} action={action}>{line}</StateLine></div>
  );
  const retry = <button type="button" className="btn-link text-meta max-lg:py-2.5" onClick={() => void q.refetch()}>Try again</button>;
  if (!d) {
    // Offline, a failed first read is the paused one's wait (lib/read-state.ts offlineFailure): the shell reads again when back.
    if (q.isError && offlineFailure(q.error)) return box(<>No connection — {noun} read when back.</>);
    if (q.isError) { const f = failSaid(`Couldn't read ${noun}`, q.error); return box(f.line, f.more, retry); }
    if (q.fetchStatus === 'paused') return box(<>No connection — {noun} read when back.</>);
    return null;
  }
  if (q.isError) return box(<>List refresh failed — read {readWords}.</>, failMore(q.error), retry);
  if (q.fetchStatus === 'paused') return box(<>No connection — list read {readWords}.</>, 'It is read again as soon as the connection is back.');
  if (!fresh) return box(<>Stale list — read {readWords}.</>);
  return null;
}

/** A first read on its way: the panel's skeleton — never "empty". */
const firstRead = (q: UseQueryResult<unknown>, read: Read<unknown>, what: string) =>
  (!read.d && !q.isError && q.fetchStatus !== 'paused' ? <Panel flush><SkeletonRows what={what} rows={4} /></Panel> : null);

// ── Needs you ────────────────────────────────────────────────────────────────────────────────────────────────────

type Opening = { openLead: string | null; toggleLead: (id: string) => void };
type PageHealth = {
  held: boolean; fetch: 'fetching' | 'paused' | 'idle';
  failed: string | null; readWords: string | null; fresh: boolean; unreadable: number | null;
};
type PagerInfo = { start: number; total: number | null; size: number; canPage: boolean; items: string; go: (at: number) => void };
const pageHolds = (total: number | null, start: number, size: number): number | null =>
  (total === null ? null : Math.max(0, Math.min(size, total - start)));

/** A lead's row. */
function leadPerson(l: LeadRow, readMs: number, o: Opening): Person {
  const s = standing(l, readMs);
  const n = naming(l.facts.name, l.who, l.channel);
  const open = l.id ? () => { if (o.openLead !== l.id) o.toggleLead(l.id!); } : undefined;
  const waiting = waitingMissing(l.waitingOn, l.channel, readMs);
  return {
    key: l.id ?? l.ref,
    name: n.name,
    source: sourceOf(l),
    stage: { pill: s.pill, wait: s.wait, tone: s.tone, title: l.since ? `${s.text} — since ${fmtDate(l.since)}` : s.text },
    claim: l.claim,
    facts: l.facts,
    wallet: cardWallet(l.walletLive, l.walletCheck, l.claim, noWalletYet(l.assessment), readMs),
    walletAt: l.wallet ?? null,
    // What is missing: what the chat waits on them for; else, ready, the team's decision; else an escalation's reason.
    missing: waiting ?? (l.ready ? { text: 'Ready for your decision', tone: 'plain' } : s.why ? { text: s.why, tone: 'plain' } : null),
    checklist: l.checklist,
    assessment: l.assessment,
    strip: flagsOf(l),
    noPath: noPathOf(l) || l.labels.some((x) => x.key === 'no_contact_path'),
    chat: <ChatButton t={targetOf(l, { sessionId: l.id }, n.name)} />,
    links: cardLink(l.cardUrl),
    open,
    isOpen: Boolean(l.id) && o.openLead === l.id,
  };
}

/** Needs you — the queues as chips with their counts; the leads in the worker's order, those ready for a decision first
 *  under their own head (the operator, 14 Oct 2026: "latest first + ready to act"). */
function NeedsView({ q, read, paged, pagedRead, chosen, from, later, setParams, ...o }: {
  q: UseQueryResult<LeadsList>; read: Read<LeadsList>; paged: UseQueryResult<LeadsList>; pagedRead: Read<LeadsList>;
  chosen: QueueInfo | null; from: number; later: boolean; setParams: SetParams;
} & Opening) {
  const { d, readMs } = read;
  const go = (c: Chip | null, at = 0) => setParams({ queue: c, from: c && at > 0 ? String(at) : null, lead: null, tab: null });
  const counted = (s: QueueInfo) => (d ? d.counts[s.count] : null);
  const size = d?.page?.size ?? pagedRead.d?.page?.size ?? 100;
  const canPage = !!(d?.page ?? pagedRead.d?.page);
  const totalOf = (s: QueueInfo) => (later && s === chosen ? (pagedRead.d ? pagedRead.d.counts[s.count] : counted(s)) : counted(s));
  const rows: LeadRow[] = later
    ? (pagedRead.d?.leads ?? []).filter((l) => l.queue === chosen!.queue)
    : d ? d.leads.filter((l) => !chosen || l.queue === chosen.queue) : [];
  const rowsReadMs = later ? pagedRead.readMs : readMs;
  const scope = chosen ? [chosen] : QUEUES;
  const due = scope.every((s) => totalOf(s) !== null)
    ? scope.reduce((n, s) => n + Math.max(0, Math.min((totalOf(s) ?? 0) - (s === chosen ? from : 0), size)), 0)
    : null;
  const pageRead = !later || paged.isSuccess;
  const mismatch = d && pageRead && due !== null && due !== rows.length && (rows.length > 0 || (!chosen && due > 0))
    ? rows.length < due
      ? `${plural(due - rows.length, "counted lead isn't", "counted leads aren't")} listed — see the ops group.`
      : `${plural(rows.length - due, 'lead', 'leads')} more than counted.`
    : null;
  const page: PageHealth | null = later
    ? {
        held: !!pagedRead.d, fetch: paged.fetchStatus, failed: paged.isError ? failWords(paged.error) : null,
        readWords: pagedRead.readWords, fresh: pagedRead.fresh, unreadable: pagedRead.d ? pagedRead.d.unreadable : 0,
      }
    : null;
  const people = rows.map((l) => leadPerson(l, rowsReadMs, o));
  const ready = rows.map((l) => l.ready);
  const later1 = from + 1;
  return (
    <>
      <ReadLines q={q} read={read} noun="the leads" />
      {d && (
        <nav className="mb-4 flex flex-wrap items-center gap-2" aria-label="Queues">
          <button type="button" className={`chip ${!chosen ? 'chip-on' : ''}`} aria-pressed={!chosen} onClick={() => go(null)}>All</button>
          {QUEUES.map((s) => {
            const n = counted(s);
            return (
              <button key={s.chip} type="button" className={`chip ${chosen === s ? 'chip-on' : ''}`} aria-pressed={chosen === s} title={s.name}
                aria-label={n === null ? `${s.name} — count could not be read` : `${s.name}: ${n}`} onClick={() => go(s.chip)}>
                {s.short}<span className="font-mono tabular">{n === null ? '?' : count(n)}</span>
              </button>
            );
          })}
        </nav>
      )}
      {d && d.unreadable === null && (
        <div className="mb-4 rounded-lg border border-warn/25 bg-warn-bg px-4 py-3" data-box=""><StateLine tone="warn" more="The worker did not say how many records it could not read.">Counts may be higher — unread records not known.</StateLine></div>
      )}
      {d && d.unreadable !== null && d.unreadable > 0 && (
        <div className="mb-4 rounded-lg border border-warn/25 bg-warn-bg px-4 py-3" data-box="">
          <StateLine tone="warn" more={`Look for ${d.unreadable === 1 ? 'it' : 'them'} in the ops group.`}>
            Counts may be higher — {plural(d.unreadable, 'record', 'records')} unread.
          </StateLine>
        </div>
      )}
      {mismatch && <p className="mb-4 text-meta text-warn" data-note="">{mismatch}</p>}
      {page && <PageLines page={page} from={later1} holds={rows.length > 0} />}
      {firstRead(q, read, 'the leads')}
      {rows.length > 0 ? (
        <Panel flush>
          <Heads />
          {ready.some(Boolean) ? (
            <>
              {[{ key: 'ready', title: 'Ready for your decision', on: true }, { key: 'rest', title: 'Everyone else', on: false }].map((part) => {
                const ps = people.filter((_, i) => ready[i] === part.on);
                return ps.length === 0 ? null : (
                  <div key={part.key} data-needs-part={part.key}>
                    <h3 className="border-b border-stroke-soft px-4 pb-2 pt-4 text-label font-medium uppercase tracking-wider text-tg-hint lg:px-5">
                      {part.title} <span className="font-mono tabular">{count(ps.length)}</span>
                    </h3>
                    <PersonList label={part.title} people={ps} />
                  </div>
                );
              })}
            </>
          ) : <PersonList label="Needs you" people={people} />}
        </Panel>
      ) : page && !page.held && !page.failed ? (
        page.fetch === 'paused'
          ? <div data-note=""><StateLine tone="warn">No connection — leads from {later1} read when back.</StateLine></div>
          : <p className="text-body text-tg-hint" data-note="">Reading the leads from {later1}…</p>
      ) : d && chosen && !page?.failed ? (
        <EmptyPage total={totalOf(chosen)} start={from} size={size} nouns={['lead', 'leads']} fewer="fewer leads now" none="Nothing in this queue." />
      ) : d && !chosen && needsTotal(d) === 0 && d.unreadable === 0 && read.fresh && !q.isError ? (
        <Panel><EmptyState>Nobody needs you.</EmptyState></Panel>
      ) : null}
      {chosen ? (
        <Pager start={from} shown={rows.length} total={totalOf(chosen)} size={size} canPage={canPage} items={chosen.items} go={(at) => go(chosen.chip, at)} />
      ) : (
        d && QUEUES.map((s) => {
          const n = counted(s);
          const listed = d.leads.filter((l) => l.queue === s.queue).length;
          return n !== null && n > size && canPage ? (
            <p key={s.queue} className="mt-3 text-meta text-tg-hint" data-note="">
              {s.short}: first {count(Math.min(size, listed))} of {count(n)}.{' '}
              <button type="button" className="btn-link max-lg:py-2.5" aria-label={`Next ${Math.min(size, n - size)} ${s.items}`} onClick={() => go(s.chip, size)}>
                Next {count(Math.min(size, n - size))} →
              </button>
            </p>
          ) : null;
        })
      )}
    </>
  );
}

/** A page with no row to show, said by the count read with it — never "nothing" for rows that could not be shown. */
function EmptyPage({ total, start, size, nouns, fewer, none }: {
  total: number | null; start: number; size: number; nouns: [string, string]; fewer: string; none: string;
}) {
  const due = pageHolds(total, start, size);
  if (due !== null && due > 0) {
    return <div data-note=""><StateLine tone="warn">{due === 1 ? `The ${nouns[0]} here could not be shown.` : `None of the ${count(due)} ${nouns[1]} could be shown.`}</StateLine></div>;
  }
  if (start > 0) return <div data-note=""><StateLine tone="neutral">{due === 0 ? `Nothing on this page — ${fewer}.` : 'Nothing on this page.'}</StateLine></div>;
  return due === 0
    ? <div data-note=""><Panel><EmptyState>{none}</EmptyState></Panel></div>
    : <div data-note=""><StateLine tone="neutral">Nothing listed — count not read.</StateLine></div>;
}

/** What is wrong with a page read on, of its own read. */
function PageLines({ page, from, holds }: { page: PageHealth; from: number; holds: boolean }) {
  const again = page.held && page.fetch === 'fetching';
  const waiting = page.held && page.fetch === 'paused';
  const lines: Array<{ line: ReactNode; more?: ReactNode }> = [];
  if (page.failed) {
    const f = failLine(`Couldn't read leads from ${from}`, page.failed);
    lines.push(holds && page.readWords
      ? { line: <>Page refresh failed — read {page.readWords}.</>, more: sentenceOf(page.failed) }
      : { line: f.line, more: f.more });
  } else if (waiting) {
    lines.push({ line: <>No connection — page read {page.readWords}.</>, more: 'It is read again as soon as the connection is back.' });
  } else if (page.readWords && !page.fresh) {
    lines.push({ line: <>Stale page — read {page.readWords}.</> });
  }
  if (page.readWords && page.unreadable === null) {
    lines.push({ line: <>Page may be missing leads — unread not known.</>, more: 'The worker did not say how many records it could not read.' });
  } else if (page.readWords && page.unreadable) {
    lines.push({ line: <>Page may be missing leads — {plural(page.unreadable, 'record', 'records')} unread.</>, more: `Look for ${page.unreadable === 1 ? 'it' : 'them'} in the ops group.` });
  }
  return (
    <>
      {lines.length > 0 && (
        <div className="mb-4 space-y-1" data-page-lines="">
          {lines.map((x, i) => <StateLine key={i} tone="warn" more={x.more}>{x.line}</StateLine>)}
        </div>
      )}
      {again && <p className="mb-4 text-meta text-tg-hint" data-note="">Reading the leads from {from} again…</p>}
    </>
  );
}

/** "Showing 101–140 of 140" and the way to the page before or after. Moving reads another page; it changes nothing. */
function Pager({ start, shown, total, size, canPage, items, go }: PagerInfo & { shown: number }) {
  const end = total === null ? null : Math.min(start + size, total);
  const missing = end === null ? 0 : Math.max(0, end - start - shown);
  const showing = end !== null && end > start && shown > 0 && (start > 0 || (total ?? 0) > size || missing > 0);
  const before = Math.min(size, start);
  const next = start + size;
  const after = canPage && total !== null && next < total ? Math.min(size, total - next) : 0;
  if (!showing && start === 0 && after === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-meta">
      {showing && (
        <span className="tabular text-tg-hint">
          Showing {count(start + 1)}–{count(end!)} of {count(total!)}{missing > 0 && <> ({count(missing)} not shown)</>}
        </span>
      )}
      {start > 0 && (
        <button type="button" className="btn-link max-lg:py-2.5" aria-label={`Previous ${before} ${items}`} onClick={() => go(start - before)}>← Previous {count(before)}</button>
      )}
      {after > 0 && (
        <button type="button" className="btn-link max-lg:py-2.5" aria-label={`Next ${after} ${items}`} onClick={() => go(next)}>Next {count(after)} →</button>
      )}
    </div>
  );
}

// ── Potential ────────────────────────────────────────────────────────────────────────────────────────────────────

/** When they last wrote (to the read) — or that they never did — and how many answers they still owe, which in words. */
function prospectStanding(p: ProspectRow, readMs: number): { text: string; why: string | null } {
  const wrote = p.lastCustomerMessageAt ? `Wrote ${atRead(p.lastCustomerMessageAt, readMs)}` : p.customerMessages === 0 ? 'Never wrote' : 'In the chat';
  const togo = p.missing === null ? null : p.missing.length === 0 ? 'every answer given' : `${plural(p.missing.length, 'answer', 'answers')} to go`;
  return { text: [wrote, togo].filter(Boolean).join(' · '), why: p.missing && p.missing.length > 0 ? `Still to answer: ${p.missing.map(missingWord).join(', ')}` : null };
}

function prospectPerson(p: ProspectRow, readMs: number, o: Opening): Person {
  const n = naming(p.name, p.who, p.channel);
  const s = prospectStanding(p, readMs);
  const open = p.id ? () => { if (o.openLead !== p.id) o.toggleLead(p.id!); } : undefined;
  return {
    key: p.id ?? p.ref,
    name: n.name,
    source: sourceOf(p),
    stage: { pill: 'Potential', wait: s.text, tone: null },
    claim: p.claim,
    facts: p.facts,
    wallet: cardWallet(p.walletLive, p.walletCheck, p.claim, noWalletYet(p.assessment, p.missing), readMs),
    walletAt: p.wallet ?? null,
    missing: waitingMissing(p.waitingOn, p.channel, readMs) ?? (s.why ? { text: s.why, tone: 'plain' } : null),
    checklist: p.checklist,
    assessment: p.assessment,
    strip: null,
    noPath: noPathOf(p),
    chat: <ChatButton t={targetOf(p, { sessionId: p.id }, n.name)} />,
    links: null,
    open,
    isOpen: Boolean(p.id) && o.openLead === p.id,
  };
}

function PotentialView({ q, read, from, setParams, ...o }: {
  q: UseQueryResult<ProspectsList>; read: Read<ProspectsList>; from: number; setParams: SetParams;
} & Opening) {
  const { d, readMs, fresh, readWords } = read;
  const rows = d ? d.prospects : [];
  const size = d?.page?.size ?? 100;
  const go = (at: number) => setParams({ from: at > 0 ? String(at) : null, lead: null, tab: null });
  const people: Person[] = rows.map((p) => prospectPerson(p, readMs, o));
  return (
    <>
      <ReadLines q={q} read={read} noun="the potential leads" />
      {firstRead(q, read, 'the potential leads')}
      {d && (rows.length > 0 ? (
        <Panel flush><Heads /><PersonList label="Potential leads" people={people} /></Panel>
      ) : from === 0 && pageHolds(d.total, 0, size) === 0 ? (
        // Plain, never green: no potential leads is news, not good news.
        fresh && !q.isError
          ? <div data-note=""><Panel><EmptyState>No potential leads right now.</EmptyState></Panel></div>
          : <div data-note=""><StateLine tone="neutral">No potential leads — read {readWords}.</StateLine></div>
      ) : (
        <EmptyPage total={d.total} start={from} size={size} nouns={['potential lead', 'potential leads']} fewer="fewer now" none="No potential leads right now." />
      ))}
      {(d || from > 0) && <Pager start={from} shown={rows.length} total={d ? d.total : null} size={size} canPage={!!d?.page} items="potential leads" go={go} />}
    </>
  );
}

// ── Clients ──────────────────────────────────────────────────────────────────────────────────────────────────────

/** Whether anyone contacted an approved client. Only an approval made since contacts were recorded can be "not contacted". */
function contactOf(c: ClientRow, trackingSince: string | null): 'contacted' | 'not_recorded' | 'not_contacted' {
  if (c.contacted) return 'contacted';
  if (!trackingSince || !c.approvedAt || Date.parse(c.approvedAt) < Date.parse(trackingSince)) return 'not_recorded';
  return 'not_contacted';
}
function clientStanding(c: ClientRow, tracking: string | null): { text: string; tone: Tone } {
  if (c.source === 'imported') return { text: `Client from before${c.contacted?.at ? ` · contacted ${fmtDay(c.contacted.at)}` : ''}`, tone: null };
  const when = c.approvedAt ? `Approved ${fmtDay(c.approvedAt)}` : 'Approved (date not known)';
  const how = contactOf(c, tracking);
  if (how === 'contacted') return { text: `${when} · contacted${c.contacted?.at ? ` ${fmtDay(c.contacted.at)}` : ''}`, tone: null };
  return { text: `${when} · ${how === 'not_recorded' ? 'contact not recorded' : 'not contacted'}`, tone: 'warn' };
}
/** A client's stage as a pill — from before, contacted, or not (amber) — and when they were approved beside it. */
function clientStage(c: ClientRow, tracking: string | null): { pill: string; wait: string | null; tone: Tone } {
  if (c.source === 'imported') return { pill: 'From before', wait: c.contacted?.at ? `contacted ${fmtDay(c.contacted.at)}` : null, tone: null };
  const when = c.approvedAt ? `Approved ${fmtDay(c.approvedAt)}` : 'Approved, date not known';
  const how = contactOf(c, tracking);
  if (how === 'contacted') return { pill: 'Contacted', wait: when, tone: null };
  return { pill: how === 'not_recorded' ? 'Contact not recorded' : 'Not contacted', wait: when, tone: 'warn' };
}
function clientWho(c: ClientRow): string | null {
  if (c.source === 'imported') return c.summary;
  const approved = c.approvedAt ? `Approved ${fmtDate(c.approvedAt)}${c.approvedBy ? ` by ${c.approvedBy}` : ''}` : null;
  const contacted = c.contacted ? `contacted ${c.contacted.at ? fmtDate(c.contacted.at) : 'at a time not known'}${c.contacted.by ? ` by ${c.contacted.by}` : ''}` : null;
  return [approved, contacted].filter(Boolean).join(' · ') || null;
}

function clientPerson(c: ClientRow, i: number, tracking: string | null, readMs: number, o: Opening): Person {
  const n = naming(c.name, c.who, c.channel);
  const open = c.id ? () => { if (o.openLead !== c.id) o.toggleLead(c.id!); } : undefined;
  return {
    key: c.id ?? c.customerId ?? `${c.ref ?? 'x'}:${i}`,
    name: n.name,
    source: sourceOf(c),
    stage: { ...clientStage(c, tracking), title: clientWho(c) },
    claim: c.claim,
    facts: c.facts,
    wallet: cardWallet(c.walletLive, c.walletCheck, c.claim, noWalletYet(c.assessment), readMs),
    walletAt: c.wallet ?? null,
    missing: c.source === 'imported' && c.summary ? { text: c.summary, tone: 'plain' } : null,
    checklist: c.checklist,
    assessment: c.assessment,
    strip: refusalStrip(c.refusedByBot, readMs),
    noPath: noPathOf(c),
    chat: <ChatButton t={targetOf(c, { sessionId: c.id, customerId: c.customerId }, n.name)} />,
    links: (
      <>
        {cardLink(c.cardUrl)}
        {c.party && (
          <Link to={`/parties/${encodeURIComponent(c.party.slug)}?entity=${c.party.entity}`} className="btn-link whitespace-nowrap text-body max-lg:py-2.5" data-party={c.party.slug}
            aria-label={`The party of ${n.name} in the Books: ${c.party.name}`}>
            Party →
          </Link>
        )}
      </>
    ),
    open,
    isOpen: Boolean(c.id) && o.openLead === c.id,
  };
}

function ClientsView({ q, read, from, setParams, trackingFallback, ...o }: {
  q: UseQueryResult<ClientsList>; read: Read<ClientsList>; from: number; setParams: SetParams; trackingFallback: string | null;
} & Opening) {
  const { d, readMs } = read;
  const rows = d ? d.clients : [];
  const total = d && d.counts.approvedHere !== null && d.counts.imported !== null ? d.counts.approvedHere + d.counts.imported : null;
  const size = d?.page?.size ?? 100;
  const tracking = (d ? d.contactTrackingSince : null) ?? trackingFallback;
  const go = (at: number) => setParams({ from: at > 0 ? String(at) : null, lead: null, tab: null });
  const people: Person[] = rows.map((c, i) => clientPerson(c, i, tracking, readMs, o));
  return (
    <>
      <ReadLines q={q} read={read} noun="the approved clients" />
      {firstRead(q, read, 'the approved clients')}
      {d && (rows.length > 0 ? (
        <Panel flush><Heads /><PersonList label="Clients" people={people} /></Panel>
      ) : (
        <EmptyPage total={total} start={from} size={size} nouns={['approved client', 'approved clients']} fewer="fewer now" none="No approved clients yet." />
      ))}
      {(d || from > 0) && <Pager start={from} shown={rows.length} total={total} size={size} canPage={!!d?.page} items="approved clients" go={go} />}
    </>
  );
}
